/**
 * @fileoverview
 * Command bus typé — remplacement propre du `window.dispatchEvent(new CustomEvent())`
 * pour la communication shell ↔ feature (notamment pour les raccourcis clavier
 * qui doivent invoquer une action sur le composant actif).
 *
 * @remarks
 * Pourquoi pas `window.dispatchEvent` : pas de typage, pas d'autocomplétion,
 * traçabilité dégueulasse, peut percoler hors du React tree. Cf. doc 02 §5.6.
 *
 * Pourquoi pas un `EventTarget` natif : marche, mais le typage des payloads
 * est manuel. Notre `createCommandBus<TCommands>` typé est ~30 LOC et offre
 * une vraie API.
 */

import { useEffect } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════════

/**
 * Map des commandes disponibles. À étendre par module-augmentation depuis
 * chaque feature pour ajouter ses propres commandes.
 *
 * @example
 * ```ts
 * // Dans une feature : src/features/skill-up/commands.ts
 * declare module '@/app/commands/command-bus' {
 *   interface CommandRegistry {
 *     'skill-up:submit-verbalize': void;
 *     'skill-up:goto-next-node': void;
 *   }
 * }
 * ```
 */
export interface CommandRegistry {
  // Commandes du shell. Les features étendent cette map par module augmentation.

  /** Ouvre la palette de commandes, ou la ferme si elle est ouverte. */
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- `void` = commande sans payload
  'shell:toggle-command-palette': void;
  /**
   * Affiche ou masque un panneau latéral. Chaque shell l'exécute à sa façon :
   * le desktop bascule le layout store, la tablette son tiroir unique.
   */
  'shell:toggle-panel': 'left' | 'right';
  /**
   * Navigue vers un chemin interne. Pour les handlers déclarés hors de React
   * (`manifest.shortcuts`), qui n'ont pas accès à `useNavigate`.
   */
  'shell:navigate': string;
}

type CommandName = keyof CommandRegistry;
type CommandPayload<K extends CommandName> = CommandRegistry[K];

// "void" dans CommandRegistry = pas de payload. On wrap en tuple pour bloquer
// le distributive conditional, et on désactive la rule no-invalid-void-type
// qui interdit `void` comme élément de tuple — ici c'est volontaire et idiomatique.
/* eslint-disable @typescript-eslint/no-invalid-void-type */
type Handler<TPayload> = [TPayload] extends [void] ? () => void : (payload: TPayload) => void;

type EmitArgs<K extends CommandName> = [CommandPayload<K>] extends [void]
  ? []
  : [payload: CommandPayload<K>];
/* eslint-enable @typescript-eslint/no-invalid-void-type */

// ════════════════════════════════════════════════════════════════════════════
// Bus typé
// ════════════════════════════════════════════════════════════════════════════

interface CommandBus {
  /**
   * Émet une commande. Tous les handlers enregistrés via `on()` sont invoqués
   * synchroniquement, dans l'ordre d'enregistrement.
   */
  emit<K extends CommandName>(name: K, ...args: EmitArgs<K>): void;

  /**
   * Enregistre un handler. Retourne une fonction de désinscription.
   * Préférer `useCommand()` dans les composants React (gère le cleanup).
   */
  on<K extends CommandName>(name: K, handler: Handler<CommandPayload<K>>): () => void;
}

function create(): CommandBus {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlers = new Map<CommandName, Set<(...args: any[]) => void>>();

  return {
    emit(name, ...args) {
      const set = handlers.get(name);
      if (!set) return;
      for (const h of set) {
        h(...args);
      }
    },
    on(name, handler) {
      let set = handlers.get(name);
      if (!set) {
        set = new Set();
        handlers.set(name, set);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const wrapped = handler as (...args: any[]) => void;
      set.add(wrapped);
      return () => {
        set.delete(wrapped);
      };
    },
  };
}

/**
 * Singleton command bus de l'app. Instance unique partagée.
 */
export const commandBus: CommandBus = create();

// ════════════════════════════════════════════════════════════════════════════
// Hook React
// ════════════════════════════════════════════════════════════════════════════

/**
 * S'abonne à une commande pendant la durée de vie du composant.
 * Désinscription automatique au unmount.
 *
 * @example
 * ```tsx
 * function VerbalizeNode() {
 *   const handleSubmit = () => { ... };
 *   useCommand('skill-up:submit-verbalize', handleSubmit);
 *   return <textarea ... />;
 * }
 * ```
 */
export function useCommand<K extends CommandName>(
  name: K,
  handler: Handler<CommandPayload<K>>,
): void {
  useEffect(() => {
    return commandBus.on(name, handler);
  }, [name, handler]);
}

// ════════════════════════════════════════════════════════════════════════════
// Test helper
// ════════════════════════════════════════════════════════════════════════════

/**
 * Crée un bus indépendant pour les tests (n'utilise pas le singleton).
 * @internal
 */
export function createCommandBusForTests(): CommandBus {
  return create();
}
